import { useEffect, useState, useRef } from 'react';
import { languagesList } from '@common/language';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { IconArrowRight, IconLoader2 } from '@tabler/icons-react';
import axios, { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import useTheme from 'chat-ui/src/hooks/useTheme';
import DOMPurify from 'dompurify';
import { StatusCodes } from 'http-status-codes';
import getTextColor from 'chat-ui/src/hooks/getTextColorContrast';
import useChatStore from 'chat-ui/src/store';
import useChatSettingsStore from 'chat-ui/src/ChatSettingsStore';
import { iframeLocalStorage } from 'chat-ui/src/utils/iframeLocalStorage';
import { toast } from 'react-toastify';
import { ERROR_MESSAGES } from '@common/constants';
import { getErrorMessage, useMinimumLoaderTime } from '@common/utils';
import { ECompanyThemeName, LogoVariant } from '@/enums/enums';
import SelectForm from '../SelectForm';
import { useConsentScreen } from '@/reactQuery/get';
import { translations } from '../translatedConsentText';
import { Checkbox } from '../ui/checkbox';
import languagesListNormalLabels from '../languageListNormalLabels';
import { ConsentScreenProps } from '@/types';
import FullPageLoader from '../FullPageLoader';
import useCompanyLogo from '@/hooks/useCompanyLogo';
import Markdown from '../markdown/Markdown';

type LanguageCode = keyof typeof translations;

function ConsentScreen({
  projectId,
  setProjectConsent,
  themeData,
  refetch,
  interviewSource,
  projectStorageSuffix,
  skipConsent = false,
  urlLanguage,
  urlInputMethods,
}: ConsentScreenProps) {
  const [isTermsChecked, setIsTermsChecked] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState({ label: '', value: '', code: '' });
  const [privacyPolicyLink, setPrivacyPolicyLink] = useState('');
  const minLoaderTime = useMinimumLoaderTime();
  const [languageFromDashboard, setLanguageFromDashboard] = useState<string>('');
  const { chat_id } = useParams();
  const navigate = useNavigate();
  const [showLanguage, setShowLanguage] = useState(false);
  const location = useLocation();
  const [browserLanguage, setBrowserLanguage] = useState('');
  const [searchParams] = useSearchParams();
  const isSandboxChat = searchParams.get('environment');
  const sandboxLink = isSandboxChat === 'sandbox';
  const { themeName } = useTheme();
  const { setCurrentConversationId } = useChatStore((state) => state);
  const { isLoading: isConsentScreenLoading, refetch: getConsentScreen } =
    useConsentScreen(chat_id);

  // Theme logo hook
  const { logo } = useCompanyLogo({
    themeData,
    variant: LogoVariant.WHITE,
  });

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const { setChatSettingsData } = useChatSettingsStore();
  const activeLanguage = showLanguage
    ? (browserLanguage as LanguageCode)
    : (languageFromDashboard as LanguageCode);
  const translation = translations[activeLanguage] || translations.en;
  const isEnglish = activeLanguage === 'en';
  const defaultLanguage = useRef<null | string>(null);

  const interviewTermsLink =
    themeName === ECompanyThemeName.EMPATHY
      ? 'https://ai-terms.empathyiq.ai/'
      : 'https://tellet.ai/interview-terms';

  const handleSubmit = () => {
    iframeLocalStorage.setItem(
      `project_consent${projectId}${sandboxLink ? '/sandbox' : ''}${
        interviewSource ? `_${interviewSource}` : ''
      }`,
      'true',
    );
    setProjectConsent(true);
  };

  const handleNavigateToErrorPage = (errorStatus?: number, errorMessage?: string) => {
    navigate(
      errorStatus
        ? `/chat/error?error_status=${errorStatus}&error-message=${errorMessage}`
        : '/chat/error-page',
    );
  };

  const sanitizedFirstParagraph = DOMPurify.sanitize(
    translation.firstParagraph
      .replace(
        '{interviewTerms}',
        `<a href="${interviewTermsLink}" target="_blank" class="font-medium underline">${translation.interviewTerms}</a>`,
      )
      .replace(
        '{privacyPolicy}',
        `<a href="${privacyPolicyLink}" target="_blank" class="font-medium underline">${translation.privacyPolicy}</a>`,
      ),
  );

  const getConsentScreenData = async () => {
    // Start a Sentry transaction for consent screen initialization
    const transaction = Sentry.startTransaction({
      name: 'consent.screen.init',
      op: 'consent.initialization',
    });

    // No need to log normal flow initialization

    try {
      const { error, data } = await getConsentScreen();

      if (error) throw error;

      if (!data) {
        handleNavigateToErrorPage();
        return;
      }

      const languageItem = languagesList?.language.filter((item) => item?.value === data?.language);
      setPrivacyPolicyLink(data.privacy_policy);
      setShowLanguage(data.allow_language_selection);

      if (!urlLanguage) {
        setSelectedLanguage(languageItem[0]);
        defaultLanguage.current = languageItem[0].label;
      } else {
        const urlLanguageOption = languagesList?.language?.find(
          (lang) =>
            lang.label.toLowerCase() === urlLanguage.toLowerCase() ||
            lang.value.toLowerCase() === urlLanguage.toLowerCase(),
        );
        if (urlLanguageOption) {
          setSelectedLanguage(urlLanguageOption);
          defaultLanguage.current = urlLanguageOption.label;
        }
      }

      const formattedDashboardLanguage = languagesListNormalLabels?.language?.find(
        (lang) => lang?.label === data?.language,
      )?.value;
      setLanguageFromDashboard(formattedDashboardLanguage as string);

      // No need to log successful data loading
    } catch (err) {
      // Capture error with enhanced context
      Sentry.withScope((scope) => {
        scope.setTag('consent_flow', 'initialization');
        scope.setContext('consent_init_context', {
          projectId: chat_id,
          isSandbox: sandboxLink,
          url: `${import.meta.env.VITE_API_BASE_URL}/projects/${chat_id}/consent_screen`,
          error: err instanceof Error ? err.message : 'Unknown error',
        });

        Sentry.captureException(err);
      });

      setPrivacyPolicyLink('');

      // handles not found error imediately on consent screen fetch
      const notFoundPageStatuses = [StatusCodes.NOT_FOUND, StatusCodes.INTERNAL_SERVER_ERROR];
      const errorStatus = err instanceof AxiosError && err.response?.status;
      if (errorStatus && notFoundPageStatuses.includes(errorStatus)) {
        handleNavigateToErrorPage(errorStatus);
      } else {
        handleNavigateToErrorPage();
      }
    } finally {
      transaction.finish();
    }
  };

  const saveChatSettings = (chatId: string | undefined, interviewType: string[]) => {
    // Use URL input methods override if provided, otherwise use project settings
    const finalInputMethods =
      urlInputMethods && urlInputMethods.length > 0 ? urlInputMethods : interviewType;
    setChatSettingsData(finalInputMethods);
    iframeLocalStorage.setItem(`chat_settings/${chatId}`, finalInputMethods.toString());
  };

  const getChat = async () => {
    // Start a Sentry transaction for getting chat after consent
    const transaction = Sentry.startTransaction({
      name: 'consent.get_chat',
      op: 'consent.submission',
    });

    // No info logging for normal flow

    try {
      const { data, error } = await refetch();

      // handling completed and unpublished state, while max numb of respondents are already handled in axiosInterceptor
      if (error && error?.status === StatusCodes.FORBIDDEN) {
        const errorMessage = getErrorMessage(error);
        handleNavigateToErrorPage(StatusCodes.FORBIDDEN, errorMessage);
        setIsSubmitLoading(false);
        return;
      }

      // fallback language is set when no language is selected, which should be the case in one situation only: DRAFT/SANDBOX
      if (data && !!data?.length) {
        setIsSubmitLoading(false);
        iframeLocalStorage.setItem(
          `current_project_language${projectStorageSuffix}`,
          selectedLanguage?.value ?? 'English',
        );
        iframeLocalStorage.setItem(
          `current_project_language_code${projectStorageSuffix}`,
          selectedLanguage?.code ?? 'en',
        );

        // No success logging for normal flow

        handleSubmit();
        transaction.finish();
        return;
      }

      const endpoint = sandboxLink ? `/chat/sandbox/${chat_id}` : `/chat/published/${chat_id}`;

      // No API call info logging

      // Build query params, avoiding duplicate language parameter
      const existingParams = new URLSearchParams(location.search);
      if (selectedLanguage?.value) {
        existingParams.set('language', selectedLanguage.value);
      }
      const queryString = existingParams.toString();

      const res = await axios.post(
        `${import.meta.env.VITE_CHAT_URL}${endpoint}${queryString ? `?${queryString}` : ''}`,
      );

      // Response data can only be empty on DRAFT/SANDBOX mode, when no questions are set
      if (Object.keys(res?.data).length === 0 && sandboxLink) {
        toast.error('Interview questions are missing. Add them to enable testing.');
        setIsSubmitLoading(false);
        return;
      }

      if (res?.data) {
        const storageKey = `current_conversation_id/${projectStorageSuffix?.replace('/', '')}`;

        iframeLocalStorage.setItem(storageKey, res.data.conversationId);
        setCurrentConversationId(res.data.conversationId);
        saveChatSettings(chat_id, res.data.type_of_interview);

        // Store language selection in localStorage
        iframeLocalStorage.setItem(
          `current_project_language${projectStorageSuffix}`,
          selectedLanguage?.value ?? 'English',
        );
        iframeLocalStorage.setItem(
          `current_project_language_code${projectStorageSuffix}`,
          selectedLanguage?.code ?? 'en',
        );

        // Add user context to Sentry for the conversation
        Sentry.setUser({
          id: res.data.conversationId,
          project: chat_id,
          isSandbox: sandboxLink,
          language: selectedLanguage?.value,
        });

        // No success logging for successful conversation initialization
      }

      setIsSubmitLoading(false);
      handleSubmit();
    } catch (error) {
      // Capture error with enhanced context
      Sentry.withScope((scope) => {
        scope.setTag('consent_flow', 'get_chat');
        scope.setContext('get_chat_context', {
          projectId: chat_id,
          isSandbox: sandboxLink,
          selectedLanguage: selectedLanguage?.value,
          error: error instanceof Error ? error.message : 'Unknown error',
        });

        Sentry.captureException(error);
      });
      console.error('Error fetching chat:', error);

      if (error instanceof AxiosError && error.code === 'ERR_NETWORK') {
        toast.error(ERROR_MESSAGES.NETWORK_ERROR);
        setIsSubmitLoading(false);
        return;
      }
      setIsSubmitLoading(false);
      throw error;
    } finally {
      transaction.finish();
    }
  };

  // eslint-disable-next-line consistent-return
  const onSubmit = async () => {
    // Start a Sentry transaction for consent submission
    const transaction = Sentry.startTransaction({
      name: 'consent.submit',
      op: 'consent.submission',
    });

    // No submission attempt logging

    setIsSubmitLoading(true);

    try {
      await getChat();
    } catch (err) {
      const axiosError = err as AxiosError;
      const chatErrorStatus = axiosError.response?.status;
      const chatErrorData = axiosError.response?.data;

      // Capture error with enhanced context
      Sentry.withScope((scope) => {
        scope.setTag('consent_flow', 'submit');
        scope.setTag('error_status', chatErrorStatus?.toString() || 'unknown');
        scope.setContext('consent_error_context', {
          projectId: chat_id,
          isSandbox: sandboxLink,
          selectedLanguage: selectedLanguage?.value,
          errorStatus: chatErrorStatus,
          errorData: chatErrorData,
        });

        // Custom fingerprinting based on error type for better grouping
        if (chatErrorStatus) {
          // Group consent errors by specific error type
          scope.setFingerprint([
            'consent-submission-error',
            `error-code-${chatErrorStatus}`,
            sandboxLink ? 'sandbox' : 'production',
          ]);
        } else {
          // Generic grouping for unknown errors
          scope.setFingerprint([
            'consent-submission-error',
            'unknown-error-type',
            sandboxLink ? 'sandbox' : 'production',
          ]);
        }

        Sentry.captureException(err);
      });

      setIsSubmitLoading(false);

      // invalid language error handling
      if (StatusCodes.BAD_REQUEST === chatErrorStatus) {
        toast.error(chatErrorData?.message || 'An error occurred while submitting consent.');
        return;
      }

      // No generic error redirect logging
      handleNavigateToErrorPage();
    } finally {
      transaction.finish();
    }
  };

  useEffect(() => {
    const { language } = navigator;
    const formattedLanguage = new Intl.Locale(language).language;
    setBrowserLanguage(formattedLanguage);
  }, []);

  useEffect(() => {
    if (skipConsent) {
      setIsTermsChecked(true);
    }

    // Dont show consent screen if skipConsent is true and urlLanguage is provided
    if (skipConsent && urlLanguage) {
      onSubmit();
    }
  }, [skipConsent, urlLanguage]);

  useEffect(() => {
    if (defaultLanguage.current !== selectedLanguage?.label && showLanguage) {
      iframeLocalStorage.setItem(`use_google_translate${projectStorageSuffix}`, 'true');
    } else {
      iframeLocalStorage.removeItem(`use_google_translate${projectStorageSuffix}`);
    }
  }, [selectedLanguage, showLanguage, projectStorageSuffix]);

  useEffect(() => {
    getConsentScreenData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSandboxChat !== null && isSandboxChat !== 'sandbox') {
      handleNavigateToErrorPage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandboxChat]);

  const isButtonDisabled = (!isTermsChecked && !skipConsent) || isSubmitLoading;

  if (isConsentScreenLoading || (skipConsent && urlLanguage) || minLoaderTime) {
    return <FullPageLoader themeData={themeData} />;
  }

  return (
    <div
      style={{
        backgroundColor: themeData?.['chat-consent-bg'] || '#FF6F98',
        color: getTextColor(themeData?.['chat-consent-bg'] || '#FFFFFF'),
      }}
      className="flex items-center justify-center relative"
    >
      {/* Consent screen content */}
      <div className="consent-content-wrapper">
        <div className="flex flex-col w-full h-full mb-8 md700:mb-4">
          <div
            dir="auto"
            className={`${isEnglish ? 'consent-markdown-content-en' : 'consent-markdown-content'}`}
          >
            <Markdown>{translation.consentText}</Markdown>
            {!urlLanguage && showLanguage && selectedLanguage?.value && (
              <>
                <p className="text-base font-light mt-10 md700:mt-6 mb-1">
                  {translation.translatePickLanguage}
                </p>
                <SelectForm
                  selectValue={selectedLanguage}
                  selectOnChange={setSelectedLanguage}
                  selectList={languagesList.language}
                />
              </>
            )}
          </div>
        </div>

        <div
          className={`${isEnglish ? 'consent-actions-container-en' : 'consent-actions-container'}`}
        >
          {!skipConsent && (
            <div className="flex items-center self-end gap-4">
              {/* eslint-disable-next-line react/no-danger */}
              <p
                dir="auto"
                className="text-base font-light text-base md800:text-sm"
                dangerouslySetInnerHTML={{ __html: sanitizedFirstParagraph }}
              />
              <Checkbox
                onClick={() => setIsTermsChecked(!isTermsChecked)}
                checked={isTermsChecked}
                style={{ borderColor: getTextColor(themeData?.['chat-consent-bg'] || '#FFFFFF') }}
                className="w-9 h-9 border-2 border-white rounded-xl data-[state=checked]:bg-white data-[state=checked]:text-chat-primary"
              />
            </div>
          )}
          {!(skipConsent && urlLanguage) && (
            <button
              type="button"
              onClick={onSubmit}
              disabled={isButtonDisabled}
              style={{
                backgroundColor: themeData?.primary,
                opacity: isTermsChecked ? 1 : 0.7,
                color: getTextColor(themeData?.primary || '#FFFFFF'),
              }}
              dir="auto"
              className="consent-submit-button"
              data-testid="consent-submit-button"
            >
              {isSubmitLoading && <IconLoader2 size={20} className="animate-spin" />}
              {translation.letsGo}
              <IconArrowRight size={20} />
            </button>
          )}
          {skipConsent && urlLanguage && isSubmitLoading && (
            <div className="flex items-center justify-center mb-20">
              <IconLoader2
                size={32}
                className="animate-spin"
                style={{ color: getTextColor(themeData?.['chat-consent-bg'] || '#FFFFFF') }}
              />
            </div>
          )}

          {/* logo */}
          {logo}
        </div>
      </div>
    </div>
  );
}

export default ConsentScreen;
